import { Language } from '@libraries/design-system';
import { constructAddressObjToString } from '@libraries/helpers';
import { PropertyType } from '@libraries/manager-client';
import { createReducer, on } from '@ngrx/store';
import * as prequalActions from '../actions/prequal.actions';
import {
  Address,
  Improvements,
  MonthlyCostInformation,
  ProgramHandbookLinks,
  Urgency,
} from '../models';

// State Interface
export interface PrequalState {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  screenActive: string;
  address?: string;
  email?: string;
  phone?: string;
  improvementList: Improvements[];
  urgency?: Urgency;
  token?: string;
  addressChoices?: Address[];
  monthlyCosts?: MonthlyCostInformation[];
  estimatedCost?: number;
  propertyType?: PropertyType;
  boundaryCode?: string;
  preferredLanguage: Language;
  spanishWorkflowEnabled?: boolean;
  spanishDocsEnabled?: boolean;
  programHandbookLinks?: ProgramHandbookLinks;
  email_communication?: boolean;
  skipApplicationAllowed?: boolean;
}

// Boiler Place States
export const initialState: PrequalState = {
  loading: false,
  error: false,
  errorMessage: '',
  screenActive: 'Main',
  improvementList: [],
  addressChoices: [],
  preferredLanguage: Language.English,
};

export const successState = {
  loading: false,
  error: false,
  errorMessage: '',
};

export const errorState = {
  loading: false,
  error: true,
};

export const showMain = { screenActive: 'Main' };
export const showDesktop = { screenActive: 'Desktop' };

// REDUCER

export const reducer = createReducer(
  initialState,
  on(prequalActions.CloseActive, prequalActions.ShowMain, (state) => {
    return {
      ...state,
      ...showMain,
    };
  }),
  on(prequalActions.ShowDesktop, (state) => {
    return {
      ...state,
      ...showDesktop,
    };
  }),
  on(prequalActions.CreateProject, (state, { payload }) => {
    return {
      ...state,
      email: payload.baseQuestions.email,
      address: payload.baseQuestions.address,
      phone: payload.baseQuestions.phone || '',
      email_communication: payload.baseQuestions.email_communication,
      improvementList: payload.improvements || [],
      urgency: payload.timeframes,
      loading: true,
    };
  }),
  on(prequalActions.SubmitIneligibleProperty, (state) => {
    return { ...state, loading: true };
  }),
  on(prequalActions.SelectAddress, (state, { payload }) => {
    return {
      ...state,
      loading: true,
      address: constructAddressObjToString(payload),
    };
  }),
  on(prequalActions.CreateProjectSuccess, (state, { payload }) => {
    const {
      token: projectToken,
      propertyType,
      spanishWorkflowEnabled,
      spanishDocsEnabled,
      programHandbookLinks,
      skipApplicationAllowed,
      // NOTE: Added {} object when yt-19374-pace-pause-prequal-updates is on,
      // Server will send empty response/payload,
      // Hence object destructuring will cause issue if we have undefined on right side.
    } = payload.project || {};
    const { monthly_costs: monthlyCosts, avg_improvement_amount: estimatedCost } = payload;

    return {
      ...state,
      ...successState,
      token: projectToken,
      ...(monthlyCosts && { monthlyCosts }),
      ...(estimatedCost && { estimatedCost }),
      propertyType,
      spanishWorkflowEnabled,
      spanishDocsEnabled,
      programHandbookLinks,
      skipApplicationAllowed,
    };
  }),
  on(
    prequalActions.CalculateMonthlyCost,
    prequalActions.ReCalculateMonthlyCost,
    (state, { payload }) => {
      return {
        ...state,
        loading: true,
        estimatedCost: payload.estimated_cost,
      };
    }
  ),
  on(
    prequalActions.CalculateMonthlyCostSuccess,
    prequalActions.ReCalculateMonthlyCostSuccess,
    (state, { payload }) => {
      const monthlyCosts = payload.monthly_costs;
      return {
        ...state,
        loading: false,
        monthlyCosts,
      };
    }
  ),
  on(
    prequalActions.CalculateMonthlyCostFail,
    prequalActions.ReCalculateMonthlyCostFail,
    (state) => {
      return {
        ...state,
        loading: false,
      };
    }
  ),
  on(prequalActions.MultipleAddressFound, (state, { payload }) => {
    return {
      ...state,
      ...successState,
      ...showDesktop,
      addressChoices: payload,
    };
  }),
  on(prequalActions.SubmitIneligiblePropertySuccess, (state) => {
    return { ...state, ...successState };
  }),
  on(prequalActions.CreateProjectFail, (state, { payload: { errors } }) => {
    const errorMessage = errors ? errors[0] : 'Something went wrong?';
    return { ...state, ...errorState, errorMessage };
  }),
  on(prequalActions.AddressOutOfDistrict, (state, { payload }) => {
    const { boundaryCode, propertyType } = payload;
    return {
      ...state,
      ...errorState,
      boundaryCode,
      propertyType,
      errorMessage: 'Address out Of District',
    };
  }),
  on(prequalActions.MayBeEligible, (state, { payload }) => {
    const { propertyType } = payload;
    return {
      ...state,
      ...errorState,
      propertyType,
      errorMessage: 'May be eligible',
    };
  }),
  on(prequalActions.SubmitIneligiblePropertyFail, (state) => {
    return { ...state, ...errorState, errorMessage: 'Something went wrong' };
  }),
  on(prequalActions.SaveOwnerEmailList, (state, { payload = [] }) => {
    return { ...state, ownerEmailList: payload };
  }),
  on(prequalActions.SetFromUrl, (state, { payload }) => {
    return {
      ...state,
      address: state.address || payload.address,
      email: state.email || payload.email,
      phone: state.phone || payload.phone,
    };
  }),
  on(prequalActions.SwitchPreferredLanguage, (state, { preferredLanguage }) => {
    return {
      ...state,
      preferredLanguage,
    };
  })
);
